// FO-322 (batch-36) — AVG-toestemmingen: gedeelde types + Zod (API + de drie
// clients delen deze schema's).
//
// BELANGRIJK: de toestemmings-TEKSTEN zijn DATA (in de DB, gemarkeerd concept tot
// HR/AVG-adviseur ze vaststelt) - NOOIT hardcoded. Wat hier wel in code staat zijn
// de STABIELE technische identifiers (de afdwing-keys, net als enum-waarden)
// waarop de afdwing-helper checkt, plus neutrale UI-labels en systeemgedrag-hints.

import { z } from 'zod'

// ─── Enums (gespiegeld vanuit Prisma) ────────────────────────────────────────

export const ToestemmingDoelgroepEnum = z.enum(['MEDEWERKER', 'KLANTGEBRUIKER'])
export type ToestemmingDoelgroep = z.infer<typeof ToestemmingDoelgroepEnum>

export const ToestemmingAntwoordWaardeEnum = z.enum(['VERLEEND', 'GEWEIGERD'])
export type ToestemmingAntwoordWaarde = z.infer<typeof ToestemmingAntwoordWaardeEnum>

export const ToestemmingKanaalEnum = z.enum(['PORTAL', 'KLANT_APP', 'BACKOFFICE', 'ONBOARDING'])
export type ToestemmingKanaal = z.infer<typeof ToestemmingKanaalEnum>

/**
 * De afgeleide stand van een persoon voor één type (nooit opgeslagen; de laatste
 * antwoord-rij + de huidige type-versie bepalen hem).
 */
export const TOESTEMMING_STATUSSEN = [
  'VERLEEND',
  'GEWEIGERD',
  'ONBEANTWOORD',
  'OPNIEUW_TE_BEANTWOORDEN',
] as const
export type ToestemmingStatus = (typeof TOESTEMMING_STATUSSEN)[number]

// ─── Stabiele afdwing-keys (startset) ────────────────────────────────────────
// Technische identifiers (geen legale tekst). De helper `heeftToestemming` checkt
// hierop; de features gebruiken deze constants i.p.v. losse strings. Nieuwe types
// mogen een eigen key krijgen via de beheer-UI - deze set is de startset die de
// afdwinging kent.

export const AFDWING_BEELDMATERIAAL_INTERN = 'BEELDMATERIAAL_INTERN'
export const AFDWING_BEELDMATERIAAL_EXTERN = 'BEELDMATERIAAL_EXTERN'
export const AFDWING_NAW_DELEN_KLANT = 'NAW_DELEN_KLANT'
export const AFDWING_DOCUMENTEN_DELEN_KLANT = 'DOCUMENTEN_DELEN_KLANT'
export const AFDWING_KLANT_COMMUNICATIE = 'KLANT_COMMUNICATIE_VOORKEUR'

/** De afdwing-keys die de code actief afdwingt (foto/anonimisering/deling). */
export const BEKENDE_AFDWING_KEYS = [
  AFDWING_BEELDMATERIAAL_INTERN,
  AFDWING_BEELDMATERIAAL_EXTERN,
  AFDWING_NAW_DELEN_KLANT,
  AFDWING_DOCUMENTEN_DELEN_KLANT,
  AFDWING_KLANT_COMMUNICATIE,
] as const

// ─── Labels (UI, neutraal) ───────────────────────────────────────────────────

export const TOESTEMMING_DOELGROEP_LABELS: Record<ToestemmingDoelgroep, string> = {
  MEDEWERKER: 'Medewerker',
  KLANTGEBRUIKER: 'Klantgebruiker',
}

export const TOESTEMMING_STATUS_LABELS: Record<ToestemmingStatus, string> = {
  VERLEEND: 'Toestemming gegeven',
  GEWEIGERD: 'Geweigerd',
  ONBEANTWOORD: 'Nog niet beantwoord',
  OPNIEUW_TE_BEANTWOORDEN: 'Opnieuw te beantwoorden',
}

export const TOESTEMMING_STATUS_BADGE: Record<ToestemmingStatus, 'success' | 'warning' | 'muted' | 'danger'> = {
  VERLEEND: 'success',
  GEWEIGERD: 'danger',
  ONBEANTWOORD: 'muted',
  OPNIEUW_TE_BEANTWOORDEN: 'warning',
}

export const TOESTEMMING_KANAAL_LABELS: Record<ToestemmingKanaal, string> = {
  PORTAL: 'Medewerker-app',
  KLANT_APP: 'Klantenportaal',
  BACKOFFICE: 'Backoffice (namens)',
  ONBOARDING: 'Onboarding',
}

/**
 * Neutrale systeemgedrag-hint per bekende afdwing-key: wat er gebeurt als de
 * toestemming ontbreekt/ingetrokken wordt. Beschrijft het AFDWING-gedrag (code),
 * niet de legale tekst - die is data. Onbekende keys krijgen een generieke hint.
 */
export const TOESTEMMING_INTREKKEN_GEVOLG: Record<string, string> = {
  [AFDWING_BEELDMATERIAAL_INTERN]:
    'Je foto verdwijnt uit het smoelenboek en de bellijst; collega’s zien dan je initialen.',
  [AFDWING_BEELDMATERIAAL_EXTERN]:
    'Je foto wordt niet gebruikt voor website of social media.',
  [AFDWING_NAW_DELEN_KLANT]:
    'Klanten zien je als voornaam + eerste letter van je achternaam, zonder foto.',
  [AFDWING_DOCUMENTEN_DELEN_KLANT]:
    'Nieuwe documenten (zoals aanwijzingen/certificaten) worden niet zonder aparte grondslag met klanten gedeeld.',
  [AFDWING_KLANT_COMMUNICATIE]:
    'Je ontvangt geen optionele communicatie waarvoor toestemming nodig is.',
}

export function toestemmingIntrekkenGevolg(afdwingKey: string): string {
  return (
    TOESTEMMING_INTREKKEN_GEVOLG[afdwingKey] ??
    'De functies die deze toestemming gebruiken tonen de betreffende gegevens niet meer.'
  )
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Beheer-DTO van een type (backoffice typen-beheer). */
export interface ToestemmingTypeDTO {
  id: string
  afdwingKey: string
  naam: string
  doelgroep: ToestemmingDoelgroep
  omschrijving: string
  tekst: string
  versie: number
  actief: boolean
  verplicht: boolean
  isConcept: boolean
  volgorde: number
  createdAt: string
  updatedAt: string
  /** Aantal personen dat op de huidige versie heeft geantwoord (register-hint). */
  aantalBeantwoord?: number
}

/** Zelfservice-DTO: de actuele stand van de ingelogde persoon voor één type. */
export interface ToestemmingStatusDTO {
  typeId: string
  afdwingKey: string
  naam: string
  omschrijving: string
  tekst: string
  doelgroep: ToestemmingDoelgroep
  huidigeVersie: number
  verplicht: boolean
  isConcept: boolean
  volgorde: number
  status: ToestemmingStatus
  /** Er bestaat een nieuwere tekstversie dan waarop is geantwoord (nudge). */
  moetOpnieuw: boolean
  /** Versie waarop het laatste antwoord sloeg (indien beantwoord). */
  antwoordVersie: number | null
  antwoordAt: string | null
  antwoordKanaal: ToestemmingKanaal | null
  /** Neutrale hint over wat intrekken/weigeren doet. */
  intrekkenGevolg: string
}

/** Register-regel (backoffice: per type, per persoon). */
export interface ToestemmingRegisterRegelDTO {
  antwoordId: string | null
  typeId: string
  afdwingKey: string
  typeNaam: string
  persoonType: ToestemmingDoelgroep
  persoonId: string
  persoonNaam: string
  status: ToestemmingStatus
  antwoordVersie: number | null
  huidigeVersie: number
  kanaal: ToestemmingKanaal | null
  bron: string | null
  heeftBewijs: boolean
  beantwoordOp: string | null
}

// ─── Input-schema's (API + client delen deze) ────────────────────────────────

export const CreateToestemmingTypeSchema = z.object({
  afdwingKey: z
    .string()
    .trim()
    .min(3)
    .max(64)
    .regex(/^[A-Z0-9_]+$/, 'Alleen hoofdletters, cijfers en underscores'),
  naam: z.string().trim().min(2).max(120),
  doelgroep: ToestemmingDoelgroepEnum,
  omschrijving: z.string().trim().min(2).max(500),
  tekst: z.string().trim().min(10),
  verplicht: z.boolean().optional().default(false),
  isConcept: z.boolean().optional().default(true),
  actief: z.boolean().optional().default(true),
  volgorde: z.number().int().min(0).optional().default(0),
})
export type CreateToestemmingTypeInput = z.infer<typeof CreateToestemmingTypeSchema>

export const UpdateToestemmingTypeSchema = z.object({
  naam: z.string().trim().min(2).max(120).optional(),
  omschrijving: z.string().trim().min(2).max(500).optional(),
  // Tekstwijziging bumpt de versie server-side (bestaande antwoorden -> opnieuw).
  tekst: z.string().trim().min(10).optional(),
  verplicht: z.boolean().optional(),
  isConcept: z.boolean().optional(),
  actief: z.boolean().optional(),
  volgorde: z.number().int().min(0).optional(),
  // Bij een tekstwijziging: laat de oude toestemming doorlopen (default) of laat
  // hem "hard vervallen" (dan telt hij als ONBEANTWOORD tot opnieuw beantwoord).
  hardVervallen: z.boolean().optional().default(false),
})
export type UpdateToestemmingTypeInput = z.infer<typeof UpdateToestemmingTypeSchema>

/** Zelfservice-antwoord (medewerker in de portal / klant in het klantenportaal). */
export const AnswerToestemmingSchema = z.object({
  typeId: z.string().min(1),
  antwoord: ToestemmingAntwoordWaardeEnum,
})
export type AnswerToestemmingInput = z.infer<typeof AnswerToestemmingSchema>

/**
 * Namens-registratie (HR/beheer): verplicht een bron van >= 10 tekens OF een
 * bewijs-upload. Nooit stilzwijgend aanvinken (AVG).
 */
export const NamensRegistratieSchema = z
  .object({
    typeId: z.string().min(1),
    employeeId: z.string().min(1).nullable().optional(),
    accountId: z.string().min(1).nullable().optional(),
    antwoord: ToestemmingAntwoordWaardeEnum,
    bron: z.string().trim().max(500).nullable().optional(),
    bronBewijsUrl: z.string().trim().max(1000).nullable().optional(),
  })
  .refine((v) => Boolean(v.employeeId) !== Boolean(v.accountId), {
    message: 'Kies precies één persoon (medewerker of klantgebruiker)',
    path: ['employeeId'],
  })
  .refine(
    (v) => (v.bron?.trim().length ?? 0) >= 10 || Boolean(v.bronBewijsUrl),
    {
      message: 'Vermeld een bron van minstens 10 tekens of upload een bewijsstuk',
      path: ['bron'],
    },
  )
export type NamensRegistratieInput = z.infer<typeof NamensRegistratieSchema>
